<template>
  <div ref="modal" class="modal artwork-feedback-modal" role="dialog">
    <div class="artwork-feedback-modal__modal-box modal-dialog">
      <div class="artwork-feedback-modal__modal-header">
        <div class="artwork-feedback-modal__header-content">
          <h2 class="artwork-feedback-modal__modal-title">Let's make it right!</h2>
          <h3 class="artwork-feedback-modal__modal-subtitle">
            Tell us what didn't go as expected so we can fix it.
          </h3>
        </div>
        <button class="artwork-feedback-modal__modal-button-close" type="button" @click="onClose">
          <UseSvg icon="modal-close-big" class="artwork-feedback-modal__close-icon" />
        </button>
      </div>

      <div class="artwork-feedback-modal__modal-body">
        <fieldset
          class="artwork-feedback-modal__fieldset"
          aria-labelledby="artwork-feedback-legend"
        >
          <legend id="artwork-feedback-legend" class="artwork-feedback-modal__legend">
            Please choose primary issue:
          </legend>
          <div class="artwork-feedback-modal__options-container">
            <div class="artwork-feedback-modal__options">
              <label
                v-for="option in options"
                :key="option.value"
                class="artwork-feedback-modal__option"
              >
                <input
                  type="radio"
                  v-model="selectedReason"
                  :value="option.value"
                  name="artwork-feedback"
                  class="artwork-feedback-modal__radio"
                />
                <span class="artwork-feedback-modal__option-label">{{ option.label }}</span>
              </label>
            </div>
          </div>
        </fieldset>
      </div>

      <div class="artwork-feedback-modal__modal-footer">
        <div class="artwork-feedback-modal__actions">
          <button type="button" class="artwork-feedback-modal__cancel" @click="onClose">
            Cancel
          </button>
          <button
            type="button"
            class="artwork-feedback-modal__submit"
            :disabled="!selectedReason"
            @click="onSubmit"
          >
            Send Feedback
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import UseSvg from "../vue-cart/UseSvg.vue";

const FEEDBACK_OPTIONS = [
  { value: "Background Removal", label: "Background Removal" },
  { value: "Image Blurriness", label: "Image Blurriness" }
];

const emit = defineEmits(["submit", "close"]);

const modal = ref(null);
const selectedReason = ref(null);
const closed = ref(false);

const options = computed(() => FEEDBACK_OPTIONS);

const onShow = () => {
  const $modal = $(modal.value);
  $modal.modal("show").on("hidden.bs.modal", () => {
    $modal.off("hidden.bs.modal");
    if (!closed.value) {
      emit("close");
    }
  });

  $(document).on("keydown.feedbackModal", e => {
    if (e.key === "Escape") {
      onClose();
    }
  });
};

const onClose = () => {
  $(document).off("keydown.feedbackModal");
  $(modal.value).modal("hide");
  closed.value = true;
  selectedReason.value = null;
  emit("close");
};

const onSubmit = () => {
  if (selectedReason.value) {
    emit("submit", selectedReason.value);
    $(modal.value).modal("hide");
    closed.value = true;
    selectedReason.value = null;
  }
};

onMounted(() => {
  onShow();
});
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "frontend/init/dekeo/mixin";
@import "frontend/init/jiffy/button/mixin";

.artwork-feedback-modal {
  &.modal {
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }

  &__modal-box {
    position: relative;
    max-width: 456px;
    width: 100%;
    margin: 0 auto;
    background: $white;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba($black, 0.15);

    @include with-tablet {
      width: 456px;
    }
  }

  &__modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 16px;
    border-bottom: 1px solid $gray-border;
    position: relative;
    text-align: left;
  }

  &__header-content {
    text-align: left;
    flex: 1;
  }

  &__modal-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    margin: 0 0 4px 0;
  }

  &__modal-subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $gray-body;
    margin: 0;
  }

  &__modal-button-close {
    @include clear-button;

    position: absolute;
    top: 16px;
    right: 16px;
    color: $dark-gray-bg;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $gray-body;
    }
  }

  &__close-icon {
    width: 20px;
    height: 20px;
  }

  &__modal-body {
    padding: 16px;
  }

  &__fieldset {
    border: none;
    margin: 0;
    padding: 0;
  }

  &__legend {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: $gray-body;
    margin: 0 0 12px 0;
    padding: 0;
  }

  &__options-container {
    margin: 12px 0;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__option {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 12px;
    font-size: 14px;
    color: $gray-body;
  }

  &__radio {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }

  &__option-label {
    cursor: pointer;
    user-select: none;
  }

  &__modal-footer {
    padding: 0 16px 16px 16px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  &__cancel {
    @include button-secondary;

    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
  }

  &__submit {
    @include jiffy-button;

    background-color: $black;
    color: $white;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;

    &:hover:not(:disabled) {
      background-color: $black;
      opacity: 0.9;
    }
  }
}
</style>
